import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { Add } from "@mui/icons-material"
import { Grid } from "@mui/material"
import { Button, NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { BaseSimpleDialog } from "../components/dialogs/BaseSimpleDialog"
import { KonanEmptyState, KonanEmptyStateFilter, KonanEmptyStateSearch } from "../components/utility/KonanEmptyState"
import { ProjectCard } from "../features/projects/ProjectCard"
import { ProjectCardLoader } from "../features/projects/ProjectCardLoader"
import { ProjectWorkflowCreationDialog } from "../features/projects/ProjectWorkflowCreationFlow"
import { useHandlePageQueryParam } from "../hooks/useHandlePageQueryParam"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { DeploymentList } from "../types/generated/api/DeploymentList"
import type { PaginatedDeploymentListList } from "../types/generated/api/PaginatedDeploymentListList"
import { useIsPermitted } from "../utils/PermissionsHelpers"
import { extractPageFromBackEndPaginationLink } from "../utils/genericHelpers"

/**
 * Projects screen component
 * @return  {React.ReactElement}
 */
export function Projects(): React.ReactElement {
  const queryClient = useQueryClient()

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions?.flattenedKonanPermissions)

  const location = useLocation()
  const navigate = useNavigate()

  const { t } = useTranslation("projects")

  const canAddProject = useIsPermitted("Add project", permissions.konan, flattenedKonanPermissions)

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastProjectRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isProjectsFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  const [openProjectDeletionDialog, setOpenProjectDeletionDialog] = useState<boolean>(false)

  // Search, sort, filter functionality states
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<string>("Most Recent")
  const [filterValue, setFilterValue] = useState<string[]>([])

  const [isWorkflowProjectDialogOpen, setIsWorkflowProjectDialogOpen] = useState<boolean>(false)

  // INFO:: keeping this here since it will be used in the revamp
  const [tabValue, _] = useHandlePageQueryParam(["model", "workflow"], "model")

  // saves data of project to be deleted to pass to deletion dialog
  const [projectToDelete, setProjectToDelete] = useState<{ name: string; id: string }>({ name: "", id: "" })

  const filterOptions = ["generic", "classification", "Credit Scoring"]

  // Map translated filter values to API values
  const getFilterApiValues = (filterValues: string[]): string[] => {
    return filterValues.map((value) => {
      if (value === t("filters.generic")) return "generic"
      if (value === t("filters.classification")) return "classification"
      if (value === t("filters.creditScoring")) return "credit_scoring"
      return value.replace(" ", "_")
    })
  }

  const {
    isLoading: isProjectsLoading,
    isFetching: isProjectsFetching,
    data: projects,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDeploymentListList>, AxiosError>(
    ["projects", `projects-${tabValue}`, searchValue, sortValue, filterValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchProjects({
        page: pageParam,
        category: tabValue as "model" | "workflow",
        type: getFilterApiValues(filterValue) as ("generic" | "classification" | "credit_scoring")[],
        search: searchValue,
        ordering: sortValue === "Name" ? "name" : "-created_at",
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      refetchOnMount: true,
    },
  )

  // project deletion mutation
  const deleteProjectMutation = useMutation<AxiosResponse, AxiosError>(
    () => KonanAPI.deleteProject(projectToDelete.id),
    {
      onSuccess: async () => {
        NotificationUtils.toast(t("notifications.projectDeleted"), {
          severity: "success",
        })

        setOpenProjectDeletionDialog(false)

        // Invalidate react-query queries
        await queryClient.invalidateQueries("projects")
        await queryClient.invalidateQueries(["project", projectToDelete.id])
      },
      onError: () => {
        NotificationUtils.toast(t("notifications.projectDeleteError"), {
          severity: "error",
        })
        handleDeleteProject(projectToDelete.name, projectToDelete.id)
      },
    },
  )

  // extracting the results after infinite-Query
  const adjustedProjects = useMemo(() => {
    return (
      projects?.pages.reduce((accumulator, page) => {
        return accumulator.concat(page.data.results as DeploymentList[])
      }, [] as DeploymentList[]) ?? []
    )
  }, [projects?.pages])

  const handleDeleteProject = (name: string, id: string): void => {
    setProjectToDelete({ name, id })
    setOpenProjectDeletionDialog(true)
  }

  async function deleteProject(): Promise<void> {
    await deleteProjectMutation.mutateAsync()

    //  To prevent flickering of alerts when closing the dialog
    setTimeout(() => deleteProjectMutation.reset(), 300)
  }

  // Reset filters when on workflow tab since filtering is only available for models
  useEffect(() => {
    if (tabValue === "workflow" && filterValue.length > 0) {
      setFilterValue([])
    }
  }, [filterValue.length, tabValue])

  // Update sort value when language changes
  useEffect(() => {
    setSortValue(t("sort.mostRecent"))
  }, [t])

  return (
    <div className="layoutPadding">
      <ProjectWorkflowCreationDialog
        isOpen={isWorkflowProjectDialogOpen}
        onClose={() => setIsWorkflowProjectDialogOpen(false)}
      />

      {/* Project deletion dialog */}
      {openProjectDeletionDialog && (
        <BaseSimpleDialog
          open={openProjectDeletionDialog}
          isLoading={deleteProjectMutation.isLoading}
          name={projectToDelete.name}
          mode={"project-deletion"}
          onAccept={deleteProject}
          onClose={() => setOpenProjectDeletionDialog(false)}
        />
      )}

      <Grid item mb={2}>
        <KonanSubHeader
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          sortValue={sortValue}
          setSortValue={setSortValue}
          setFilterValue={setFilterValue}
          filterValue={filterValue}
          containerType="Project"
          sortOptions={[
            {
              label: t("sort.mostRecent"),
              value: "Most Recent",
            },
            {
              label: t("sort.name"),
              value: "Name",
            },
          ]}
          showButton={canAddProject}
          filterOptions={tabValue === "model" ? filterOptions : undefined}
          isDebouncedInput
          actions={[
            canAddProject ? (
              <Button
                key={`new-${tabValue}-button`}
                onClick={
                  tabValue === "model"
                    ? () => navigate(`${location?.pathname}/new-model`)
                    : () => setIsWorkflowProjectDialogOpen(true)
                }
                variant="primary"
                startIcon={<Add fontSize="small" />}
                size="regular"
              >
                {tabValue === "model" ? t("buttons.newModel") : t("buttons.newWorkflow")}
              </Button>
            ) : (
              <></>
            ),
          ]}
        />
      </Grid>

      {/* Render projects in an infinite scrollbar for better UI/UX */}
      <Grid container spacing={2}>
        {adjustedProjects.map((project: DeploymentList, index: number) => (
          <Grid
            item
            key={project.uuid}
            xs={12}
            md={6}
            lg={4}
            ref={hasNextPage && index === adjustedProjects.length - 1 ? lastProjectRef : undefined}
          >
            <ProjectCard project={project} onProjectDeletionClicked={handleDeleteProject} />
          </Grid>
        ))}

        {/* Show loader when request is still loading OR fetching next page */}
        {(isProjectsFetching || isProjectsLoading || isFetchingNextPage) &&
          [1, 2].map((item: number) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <ProjectCardLoader isWorkflow={tabValue === "workflow"} />
            </Grid>
          ))}
      </Grid>

      {/**
       * Project screen empty states
       * 1. no projects yet
       * 2. search value couldn't be found
       * 3. filter value couldn't be found
       */}
      {projects?.pages[0]?.data.count === 0 && !isProjectsFetching ? (
        <Grid container xs={12} className={"empty-container"}>
          <KonanEmptyState title={t("emptyStates.noProjects.title")} subTitle={t("emptyStates.noProjects.subtitle")} />
        </Grid>
      ) : !isProjectsFetching && adjustedProjects?.length === 0 && (searchValue || (searchValue && filterValue)) ? (
        <Grid container xs={12} className={"empty-container"}>
          <KonanEmptyStateSearch title={searchValue} />
        </Grid>
      ) : (
        adjustedProjects?.length === 0 &&
        !isProjectsFetching &&
        filterValue &&
        !searchValue && (
          <Grid item xs={12} className={"empty-container"}>
            <KonanEmptyStateFilter title={filterValue} />
          </Grid>
        )
      )}
    </div>
  )
}
